/* eslint-disable no-console, no-unused-vars */
import React, { useEffect, useState, useMemo, use } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useSelector, useDispatch } from "react-redux";
import { Constants } from "config/constants";
import _has from "lodash/has";
import { RadioGroup } from "@headlessui/react";
import Cookies from "js-cookie";
import {
  CogIcon,
  DocumentIcon,
  FolderIcon,
  HomeIcon,
  OfficeBuildingIcon,
  CreditCardIcon,
  GiftIcon,
} from "@heroicons/react/outline";
import UserOnboarding from "components/UserOnboarding";
import DashboardWrapper from "components/lib/Global/DashboardWrapper";
import Hero from "components/lib/Global/Hero";
import { AdminDashboardView } from "./AdminDashboard";
import {
  getCompaniesToReviewAsync,
  getCompaniesForCorrectionsAsync,
  getReviewer,
} from "state/slices/tables/reviewersTable";
import { getFullName, getGreetings } from "utils";
import { getComments } from "state/slices/comment";
import ReviewApplicationList from "components/ReviewApplicationList";
import SuperAdminWelcomeScreen from "./SystemAdminWelcomeScreen";
import {
  PreviewIcon,
  SwitchHorizontal,
} from "components/lib/Shared/Icons/sflIcons";
import { SwitchHorizontalIcon } from "@heroicons/react/solid";
import { resetCompanyRelatedSlices } from "state/slices/company";
import RoleSwitch from "components/lib/Shared/RoleSwitch";
import ServicesList from "components/lib/Shared/ServicesList";
import NotificationBanner from "components/lib/Shared/NotificationBanner";
import CompanyProcesses from "components/lib/Shared/CompanyProcesses";
import { resetActiveDashboardSlice } from "state/slices/activeDashboard";
import Packages from "components/OrgAndSuperAdminDashboard/Packages";
import { getCompaniesForSignatureAsync } from "state/slices/companies";
import SignApplications from "components/SignApplications";
import { getStatsAsync } from "state/slices/stats";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import Button from "components/lib/Shared/Button";
import TemplatesSettings from "components/OrgAndSuperAdminDashboard/TemplatesSettings";
import ReviewApplicationCorrections from "components/ReviewApplicationCorrections";
import { updateTemplateState } from "state/slices/template";
import { useSocket } from "context/SocketContext";
import { setCookie } from "utils/cookie";
import ComplianceAccessGate from "components/Compliance/ComplianceAccessGate";
import OrgComplianceDashboard from "components/Compliance/OrgComplianceDashboard";
import SuperComplianceDashboard from "components/Compliance/SuperComplianceDashboard";
import {
  setIsTransferYourBusiness,
  setVerifiedRegNumber,
  setIsTransferYourBusinessFromDashboard,
  setTransferYourBusinessInitiator,
  setTransferOrgAdmin,
} from "state/slices/transferYourBusiness";
import {
  getCompaniesTransactionsCorrectionsAsync,
  getCompaniesTransactionsReviewAsync,
  getCompaniesTransactionsSignatureAsync,
} from "state/slices/companiesTransactions";
import { updateOrganizationsState } from "state/slices/organizations";
import Subscriptions from "components/OrgAndSuperAdminDashboard/Subscriptions";
import Billing from "components/OrgAndSuperAdminDashboard/Billing";

const COMPLIANCE_VIEW_LABELS = Object.freeze({
  summary: "Compliance Summary",
  metrics: "Compliance Metrics",
  tracker: "Compliance Tracker",
  analysis: "Compliance Tasks",
  settings: "Compliance Settings",
});

const SUPER_ADMIN_COMPLIANCE_VIEWS = [
  "summary",
  "metrics",
  "tracker",
  "settings",
];

const ORG_ADMIN_COMPLIANCE_VIEWS = [
  "summary",
  "tracker",
  "analysis",
  "settings",
];

const DashboardPage = () => {
  const [selectedCompanyProcess, setSelectedCompanyProcess] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [cookieRole, setCookieRole] = useState(null);
  const [selectedTab, setSelectedTab] = useState("home");
  const [annualComplianceView, setAnnualComplianceView] = useState("summary");

  const dispatch = useDispatch();
  const socket = useSocket();
  const history = useHistory();
  const location = useLocation();
  const hash = location.hash.slice(1);
  const { isAuthenticated } = useAuth0();

  const currentUser = useSelector((state) => state.userSlice);
  const { names } = currentUser || {};
  const {
    companiesTransactionsSignature,
    companiesTransactionsReview,
    companiesTransactionsCorrections,
  } = useSelector((state) => state.companiesTransactionsSlice);
  const slice = useSelector((state) => state.companiesTransactionsSlice);
  const { getUserStatus } = useSelector((state) => state.authSlice);
  const {
    organizationName,
    dashboardUrl,
    breadCrumbs,
    organization,
    dashboardRoles,
    dashboardPermissions,
  } = useSelector((state) => state.activeDashboardSlice);
  const { stats } = useSelector((state) => state.statsSlice);
  const { selectedTemplate } = useSelector((state) => state.templateSlice);
  const { reviewer } = useSelector((state) => state.reviewersTable);
  const role = Cookies.get("role");

  useEffect(() => {
    dispatch(setVerifiedRegNumber(""));
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      updateOrganizationsState({
        organizations: {
          items: currentUser?.organizations ?? [],
          totalCount: currentUser?.organizations?.length ?? 0,
        },
      })
    );
  }, [dispatch, currentUser]);

  useEffect(() => {
    if (selectedTemplate?._id) {
      socket.emit("leave-selected-template-room", selectedTemplate._id);
      socket.emit("join-selected-template-room", selectedTemplate._id);

      socket.on(
        "set-overlay-on-company-formation-second-step",
        (showOverlay) => {
          dispatch(updateTemplateState({ showOverlay }));
        }
      );
      socket.on("replace-selected-template", (template) => {
        dispatch(
          updateTemplateState({
            selectedTemplate: template,
          })
        );
      });
    }
  }, [dispatch, socket, selectedTemplate?._id]);

  useEffect(() => {
    const hiddenNotifications =
      JSON.parse(window.sessionStorage.getItem("hiddenNotifications")) || [];
    if (
      currentUser?.isProfessional &&
      !currentUser?.attachProfessionsDetails &&
      !hiddenNotifications.includes("professionalInfoMessageDashboard")
    ) {
      window.sessionStorage.setItem(
        "professionalInfoMessageDashboard",
        `You have a pending profile to complete, Please proceed to the profile page and update your professional details.`
      );
      window.dispatchEvent(new Event("storage"));
    } else {
      window.sessionStorage.removeItem("professionalInfoMessageDashboard");
    }
    if (currentUser?.email) {
      dispatch(getReviewer({ email: currentUser?.email }));
      dispatch(getCompaniesTransactionsCorrectionsAsync());
      dispatch(getCompaniesTransactionsReviewAsync());
      dispatch(getCompaniesTransactionsSignatureAsync());
    }

    if (currentUser?.role === Constants.ROLES.SYSTEM_ADMIN) {
      setCookie("role", Constants.COOKIE_ROLES.SYSTEM_ADMIN);
      setCookieRole(Constants.COOKIE_ROLES.SYSTEM_ADMIN);
    }
    if (!Cookies.get("role")) {
      setCookie("role", Constants.COOKIE_ROLES.APPLICANT);
      setCookieRole(Constants.COOKIE_ROLES.APPLICANT);
    } else {
      setCookieRole(Cookies.get("role"));
    }
  }, [
    dispatch,
    currentUser?.role,
    currentUser?.isProfessional,
    currentUser?.attachProfessionsDetails,
    currentUser?.email,
  ]);

  useEffect(() => {
    if (
      isAuthenticated &&
      (!Cookies.get("role") ||
        Cookies.get("role") === Constants.COOKIE_ROLES.APPLICANT)
    ) {
      dispatch(getStatsAsync({ correctionsCount: true }));
    }
  }, [dispatch, isAuthenticated, socket]);

  useEffect(() => {
    const hiddenNotifications =
      JSON.parse(window.sessionStorage.getItem("hiddenNotifications")) || [];
    if (
      stats.companies?.companiesNeedCorrection > 0 &&
      !hiddenNotifications.includes("applicationsNeedCorrections")
    ) {
      window.sessionStorage.setItem(
        "applicationsNeedCorrections",
        `You have ${stats.companies?.companiesNeedCorrection} ${stats.companies?.companiesNeedCorrection > 1
          ? "applications"
          : "application"
        } to correct`
      );
      window.dispatchEvent(new Event("storage"));
    } else {
      window.sessionStorage.removeItem("applicationsNeedCorrections");
    }
  }, [stats.companies]);

  useEffect(() => {
    if (
      reviewer?.companyId &&
      currentUser?.isOnboarded &&
      !currentUser?.isProfileApprovedByUser
    ) {
      // dispatch(getComments({ companyId: reviewer.companyId, createdBy: reviewer._id }));
      history.push(`/profile`);
    }
  }, [
    reviewer,
    history,
    currentUser?.isProfileApprovedByUser,
    currentUser.isOnboarded,
  ]);

  useEffect(() => {
    if (selectedCompany) {
      setCookie("role", selectedCompany.role);
      if (selectedCompany.role === Constants.ROLES.APPLICANT)
        history.push(
          `/companies/${selectedCompany?.company?._id || selectedCompany?._id}`
        );
      if (selectedCompany.role === Constants.ROLES.REVIEWER)
        if (
          selectedCompany?.transferYourBusinessInitiator ||
          selectedCompany?.company?.transferYourBusinessInitiator
        ) {
          history.push(
            `/companies/${selectedCompany?.company?._id || selectedCompany?._id
            }/transfer-review`
          );
        } else {
          history.push(
            `/companies/${selectedCompany?.company?._id || selectedCompany?._id
            }/review`
          );
        }
    }
  }, [history, selectedCompany]);

  useEffect(() => {
    if (selectedService) {
      setCookie("role", Constants.ROLES.APPLICANT);
      // reset previous company related data
      dispatch(resetCompanyRelatedSlices());
      history.push(selectedService.href);
    }
  }, [dispatch, history, selectedService]);

  useEffect(() => {
    if (selectedCompanyProcess?.href) {
      history.push(selectedCompanyProcess.href);
    }
  }, [history, selectedCompanyProcess]);

  const isProfileIncomplete = ["Employee", "Co Applicant"].includes(
    currentUser.profileType
  );

  useEffect(() => {
    // Check if the selectedCompanyProcess is "transferYourBusiness"
    if (
      !isProfileIncomplete &&
      selectedCompanyProcess?.id === "transferYourBusiness"
    ) {
      dispatch(setIsTransferYourBusiness(true));
      dispatch(setIsTransferYourBusinessFromDashboard(true));
      dispatch(
        setTransferYourBusinessInitiator({
          user: currentUser?._id,
        })
      );
      if (currentUser?.orgAdmin) {
        dispatch(setTransferOrgAdmin(currentUser.orgAdmin));
      }
      // Redirect to "/applicant-transfer-business" route
      history.push("/applicant-transfer-business");
    }
  }, [
    dispatch,
    history,
    currentUser?._id,
    selectedCompanyProcess,
    isProfileIncomplete,
  ]);

  const companyProcesses = useMemo(() => {
    let tempCompanyProcesses = [
      {
        id: "setupYourBusiness",
        title: "Set up your business",
        description:
          "Initiate a new application with just a few clicks and you will be on your way.",
        icon: <FolderIcon />,
        hash: "package-selection",
      },
      {
        id: "transferYourBusiness",
        title: "Transfer your business",
        description:
          "Initiate transfer of existing business operations or ownership to Simple Formations Limited.",
        icon: <SwitchHorizontal />,
        disabled: false,
        hash: "applicant-transfer-business",
      },
    ];

    if (
      companiesTransactionsReview.totalCount > 0 ||
      companiesTransactionsReview.queryParams?.search
    ) {
      tempCompanyProcesses.push({
        id: "reviewApplications",
        title: "Review applications",
        description: "Your application has been received and is under review.",
        icon: <OfficeBuildingIcon />,
        hash: "review-applications",
      });
    }

    if (
      companiesTransactionsCorrections.totalCount > 0 ||
      companiesTransactionsCorrections.queryParams?.search
    ) {
      tempCompanyProcesses.push({
        id: "reviewCorrections",
        title: "Review Application Corrections",
        description: "Your application has been received but need corrections.",
        icon: <OfficeBuildingIcon />,
        hash: "correct-applications",
      });
    }

    if (
      companiesTransactionsSignature.totalCount > 0 ||
      companiesTransactionsSignature.queryParams?.search
    ) {
      tempCompanyProcesses.push({
        id: "signApplication",
        title: "Signature",
        description:
          "Please sign the attached forms and resubmit them back to Admins for review",
        icon: <OfficeBuildingIcon />,
        hash: "sign-applications",
      });
    }

    return tempCompanyProcesses;
  }, [
    companiesTransactionsReview.totalCount,
    companiesTransactionsReview.queryParams?.search,
    companiesTransactionsCorrections.totalCount,
    companiesTransactionsCorrections.queryParams?.search,
    companiesTransactionsSignature.totalCount,
    companiesTransactionsSignature.queryParams?.search,
  ]);

  // Organization onboarding redirect
  const draftOrganization = currentUser?.organizations?.find(
    ({ corporate, role }) =>
      role === Constants.ROLES.CORPORATE_ORG_ADMIN &&
      corporate.status === "Draft"
  );
  useEffect(() => {
    if (draftOrganization?.corporate?._id && currentUser?.isOnboarded) {
      history.push(`/corporates/${draftOrganization?.corporate?._id}`);
    }
  }, [dispatch, history, draftOrganization, currentUser?.isOnboarded]);

  useEffect(() => {
    if (hash) {
      setSelectedCompanyProcess(
        companyProcesses.find((companyProcess) => companyProcess.hash === hash)
      );
    }
  }, [hash, companyProcesses]);

  useEffect(() => {
    if (role !== Constants.COOKIE_ROLES.BRS_OPS) return;
    if (role === Constants.COOKIE_ROLES.BRS_OPS && selectedTab !== "home") {
      setSelectedTab("home");
    }
  }, [role, selectedTab]);

  if (!_has(currentUser, "isOnboarded") || getUserStatus.status === "loading") {
    return (
      <DashboardWrapper>
        <div className="flex items-center justify-center h-screen">
          <SpinnerIcon className="text-gray-500" />
        </div>
      </DashboardWrapper>
    );
  }

  const changeApplicantAdminView = () => {
    if (role === "10") {
      setCookie("role", 7);
      setCookieRole(Constants.COOKIE_ROLES.APPLICANT);
    }
    if (role === Constants.COOKIE_ROLES.APPLICANT) {
      setCookie("role", 10);
      setCookieRole("10");
    }
    setSelectedCompanyProcess(null);
  };

  const completedCompanies = currentUser?.linkedCompanies?.filter(
    ({ company }) =>
      company?.status === "Active" &&
      company?.createdBy?._id !== currentUser?._id
  );

  // Render user onboarding form if not onboarded
  if (currentUser && !currentUser?.isOnboarded) {
    return (
      <DashboardWrapper
        showSystemAdminBar={role === Constants.COOKIE_ROLES.SYSTEM_ADMIN}
      >
        <div className="grid mx-auto">
          <UserOnboarding />
        </div>
      </DashboardWrapper>
    );
  }

  if (
    [
      Constants.COOKIE_ROLES.SUPER_ADMIN,
      Constants.COOKIE_ROLES.ORG_ADMIN,
      Constants.COOKIE_ROLES.BRS_OPS,
    ].includes(role)
  ) {
    const handleExitDashboard = () => {
      dispatch(resetActiveDashboardSlice());
      setCookieRole(Constants.COOKIE_ROLES.APPLICANT);
      setCookie("role", Constants.COOKIE_ROLES.APPLICANT);
      history.push(dashboardUrl);
    };

    let navigation = [
      {
        id: "home",
        title: "Home",
        Icon: HomeIcon,
      },
    ];

    if (role !== Constants.COOKIE_ROLES.BRS_OPS) {
      navigation.push(
        {
          id: "annualCompliance",
          title: "Annual Compliance",
          Icon: FolderIcon,
        },
        {
          id: "subscriptions",
          title: "Subscriptions",
          Icon: CreditCardIcon,
        },
        {
          id: "packages",
          title: "Products",
          Icon: GiftIcon,
        },
        {
          id: "billing",
          title: "Billing",
          Icon: DocumentIcon,
        },
        {
          id: "templateSettings",
          title: "Templates",
          Icon: DocumentIcon,
        }
      );
    }

    return (
      <DashboardWrapper
        pages={breadCrumbs}
        showBreadCrumbs
        exitDashboardText="Exit back to Company Dashboard"
        handleExitDashboard={handleExitDashboard}
      >
        <div className="py-10 mx-auto">
          <NotificationBanner
            key="flashMessageDashboard"
            notificationKey="flashMessageDashboard"
          />
          <NotificationBanner
            key="professionalInfoMessageDashboard"
            notificationKey="professionalInfoMessageDashboard"
            handleView={() => history.push(`/profile`)}
          />
          <div className="flex flex-col justify-between sm:flex-row">
            <h1 className="text-4xl font-semibold text-primary">
              {organizationName}
            </h1>
            <div className="flex flex-col justify-end my-6 mb-4 lg:flex-row sm:my-0">
              <RoleSwitch
                setCookieRole={setCookieRole}
                setSelectedCompanyProcess={setSelectedCompanyProcess}
                organizationName={organizationName}
                organization={organization}
                breadCrumbs={breadCrumbs}
                dashboardUrl={dashboardUrl}
                dashboardRoles={dashboardRoles}
                dashboardPermissions={dashboardPermissions}
              />
            </div>
          </div>
          <div className="flex mb-8 space-x-4 font-normal border-b border-gray-200 text-tertiary">
            {navigation.map(({ id, title, Icon }) => (
              <Button
                key={id}
                overrideBaseStyles
                onClick={() => {
                  setSelectedTab(id);
                  if (id !== "annualCompliance") {
                    setAnnualComplianceView("summary");
                  }
                }}
                className={`flex space-x-2 items-center px-1 pb-1 ${selectedTab === id
                  ? "text-primary border-b-[3px] border-primary font-medium"
                  : ""
                  }`}
              >
                <Icon
                  className={`w-5 h-5 ${selectedTab === id ? "text-secondary" : ""
                    }`}
                  aria-hidden="true"
                />
                <span>{title}</span>
              </Button>
            ))}
          </div>
          {selectedTab === "home" && <AdminDashboardView role={role} />}
          {selectedTab === "packages" && <Packages />}
          {selectedTab === "templateSettings" && <TemplatesSettings />}
          {selectedTab === "subscriptions" && <Subscriptions />}
          {selectedTab === "billing" && <Billing />}
          {selectedTab === "annualCompliance" && (
            <ComplianceAccessGate allowAdminBypass>
              <div className="space-y-8 py-10">
                <Hero
                  heading={
                    role === Constants.COOKIE_ROLES.SUPER_ADMIN
                      ? "SuperAdmin Compliance Summary"
                      : "OrgAdmin Compliance Summary"
                  }
                  description={
                    role === Constants.COOKIE_ROLES.SUPER_ADMIN
                      ? "Portfolio-level compliance telemetry across all organizations."
                      : "Aggregated compliance insights across assigned corporates."
                  }
                />
                <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2">
                  {(role === Constants.COOKIE_ROLES.SUPER_ADMIN
                    ? SUPER_ADMIN_COMPLIANCE_VIEWS
                    : ORG_ADMIN_COMPLIANCE_VIEWS
                  ).map((viewKey) => {
                    const isActive = annualComplianceView === viewKey;
                    return (
                      <button
                        key={viewKey}
                        type="button"
                        onClick={() => setAnnualComplianceView(viewKey)}
                        className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${isActive
                          ? "bg-sflPrimary text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                      >
                        {COMPLIANCE_VIEW_LABELS[viewKey]}
                      </button>
                    );
                  })}
                </div>
                {role === Constants.COOKIE_ROLES.SUPER_ADMIN ? (
                  <SuperComplianceDashboard activeView={annualComplianceView} />
                ) : (
                  <OrgComplianceDashboard
                    organization={organization}
                    activeView={annualComplianceView}
                  />
                )}
              </div>
            </ComplianceAccessGate>
          )}
        </div>
      </DashboardWrapper>
    );
  }

  const handleCompanyProcessSelection = (companyProcess) => {
    if (companyProcess.hash) {
      window.location.hash = companyProcess.hash;
    } else setSelectedCompanyProcess(companyProcess);
  };

  return (
    <DashboardWrapper
      showSystemAdminBar={role === Constants.COOKIE_ROLES.SYSTEM_ADMIN}
    >
      <div className="py-10 mx-auto">
        <NotificationBanner
          key="flashMessageDashboard"
          notificationKey="flashMessageDashboard"
        />
        <NotificationBanner
          key="professionalInfoMessageDashboard"
          notificationKey="professionalInfoMessageDashboard"
          handleView={() => history.push(`/profile`)}
        />
        <NotificationBanner
          key="applicationsNeedCorrections"
          notificationKey="applicationsNeedCorrections"
          handleView={() => history.push(`/transactions`)}
          type="info"
        />
        {role === "10" && <AdminDashboardView role={role} />}
        {role === Constants.COOKIE_ROLES.SYSTEM_ADMIN && (
          <SuperAdminWelcomeScreen />
        )}
        {
          <div className="my-10 text-sflPrimary">
            <RadioGroup
              value={selectedCompanyProcess}
              onChange={handleCompanyProcessSelection}
              className="my-4"
            >
              <RadioGroup.Label className="text-base">
                <div>
                  <h2 className="pb-4 font-semibold text-title5">
                    {getGreetings()}, {getFullName(names)}
                  </h2>
                  <h1 className="font-medium">
                    What would you like to do today?
                  </h1>
                </div>
              </RadioGroup.Label>
              <CompanyProcesses companyProcesses={companyProcesses} />
            </RadioGroup>
          </div>
        }
        {selectedCompanyProcess?.id === "setupYourBusiness" && (
          <>
            {isProfileIncomplete ||
              (currentUser?.status === "Pending Review" &&
                currentUser?.companies.length >=
                Constants.COMPANIES_LIMIT_FOR_UNVERIFIED_USER) ? (
              <>
                {isProfileIncomplete ? (
                  <span className="font-medium text-red-500">
                    You cannot make this application until your profile is
                    upgraded.{" "}
                    <Link to="/profile" className="underline">
                      Click here to edit your profile.
                    </Link>
                  </span>
                ) : (
                  <>
                    <span className="font-medium text-red-500">
                      You can only create one company until your profile is
                      verified.
                    </span>

                    <div className="mt-4">
                      <Link to="/transactions" className="">
                        <div className="inline-flex text-white button button-hover button-disabled button-outline button-focus bg-sflPrimary">
                          <div className="flex items-center gap-2">
                            <PreviewIcon />
                            View Transactions
                          </div>
                        </div>
                      </Link>
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                {role !== Constants.COOKIE_ROLES.SYSTEM_ADMIN && (
                  <ServicesList
                    selectedService={selectedService}
                    setSelectedService={setSelectedService}
                    className="mb-4"
                  />
                )}
              </>
            )}
          </>
        )}
        {selectedCompanyProcess?.id === "transferYourBusiness" && (
          <>
            {isProfileIncomplete ? (
              <span className="font-medium text-red-500">
                You cannot transfer your business until your profile is{" "}
                <Link to="/profile" className="underline">
                  upgraded.
                </Link>
              </span>
            ) : (
              <></>
            )}
          </>
        )}

        {selectedCompanyProcess?.id === "reviewApplications" && (
          <RadioGroup value={selectedService} onChange={setSelectedCompany}>
            <RadioGroup.Label className="font-medium text-gray-900">
              Application Review
            </RadioGroup.Label>
            <div className="mt-2">
              <ReviewApplicationList setSelectedCompany={setSelectedCompany} />
            </div>
          </RadioGroup>
        )}
        {selectedCompanyProcess?.id === "reviewCorrections" && (
          <RadioGroup value={selectedService} onChange={setSelectedCompany}>
            <RadioGroup.Label className="font-medium text-gray-900">
              Application Corrections Review
            </RadioGroup.Label>
            <div className="mt-2">
              <ReviewApplicationCorrections
                setSelectedCompany={setSelectedCompany}
              />
            </div>
          </RadioGroup>
        )}
        {selectedCompanyProcess?.id === "signApplication" && (
          <RadioGroup value={selectedService} onChange={setSelectedCompany}>
            <div className="mt-2">
              <SignApplications />
            </div>
          </RadioGroup>
        )}
        {(completedCompanies.length > 0 ||
          currentUser?.linkedOrganizations.length > 0 ||
          currentUser?.myCompanies?.length > 0) &&
          role === Constants.COOKIE_ROLES.APPLICANT && (
            <div className="grid gap-4 mt-4">
              <div>
                <h1 className="font-medium text-gray-900">Summary</h1>
              </div>
              <div>
                <div className="grid grid-cols-3 gap-6">
                  {/* Completed card */}
                  <div className="p-6 space-y-4 bg-white rounded-md shadow-sm">
                    <Link to="/companies">
                      <div>
                        <span className="text-xs font-medium uppercase">
                          APPROVED COMPANIES
                        </span>
                        <h1 className="text-7xl font-base">
                          {completedCompanies.length +
                            currentUser?.linkedOrganizations.length +
                            currentUser?.myCompanies?.length ?? 0}
                        </h1>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
      </div>
    </DashboardWrapper>
  );
};

export default DashboardPage;
